import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import type {
  ChecklistItem,
  ChecklistItemInput,
  ChecklistView,
  RowState,
} from '@internal/backstage-plugin-bidone-backend';
import { useAuthedFetch } from '../useAuthedFetch';
import { checklistKey } from './keys';

type SectionLockArgs = { release: string; sectionId: string };
type SectionSaveArgs = {
  release: string;
  sectionId: string;
  // New section title; omit to leave the existing title untouched.
  sectionText?: string;
  items: ChecklistItemInput[];
};
type SetStateArgs = { release: string; itemId: string; state: RowState };

// Section endpoints all live under
// `/releases/<release>/sections/<sectionId>/...` — the small helper keeps
// the four hooks below from each re-doing the encodeURIComponent dance.
const sectionUrl = (release: string, sectionId: string, suffix = '') =>
  `/releases/${encodeURIComponent(release)}/sections/${encodeURIComponent(
    sectionId,
  )}${suffix}`;

// Acquires the per-section lock. 409 if another user holds it.
export const useLockSection = (): UseMutationResult<
  ChecklistView,
  Error,
  SectionLockArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, sectionId }) =>
      authedFetch(sectionUrl(release, sectionId, '/lock'), { method: 'POST' }),
    onSuccess: (data, { release }) => {
      qc.setQueryData(checklistKey(release), data);
    },
  });
};

// Releases the per-section lock — used by the section's Cancel button.
export const useUnlockSection = (): UseMutationResult<
  ChecklistView,
  Error,
  SectionLockArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, sectionId }) =>
      authedFetch(sectionUrl(release, sectionId, '/unlock'), {
        method: 'POST',
      }),
    onSuccess: (data, { release }) => {
      qc.setQueryData(checklistKey(release), data);
    },
  });
};

// Saves the step rows of one section block; backend releases the lock on
// success. Payload is steps-only — section headers themselves can't be
// renamed via this endpoint (template-driven).
export const useSaveSection = (): UseMutationResult<
  ChecklistView,
  Error,
  SectionSaveArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, sectionId, sectionText, items }) =>
      authedFetch(sectionUrl(release, sectionId, '/checklist'), {
        method: 'PUT',
        json: sectionText !== undefined ? { sectionText, items } : { items },
      }),
    onSuccess: (data, { release }) => {
      qc.setQueryData(checklistKey(release), data);
    },
  });
};

// Cascade-deletes a section + all its step rows. Requires the section lock.
export const useDeleteSection = (): UseMutationResult<
  ChecklistView,
  Error,
  SectionLockArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, sectionId }) =>
      authedFetch(sectionUrl(release, sectionId), { method: 'DELETE' }),
    onSuccess: (data, { release }) => {
      qc.setQueryData(checklistKey(release), data);
    },
  });
};

// Per-row state mutation. Independent of the section lock — used by the
// BDirect table's State dropdown (Not ready / No changes / Tagged). On
// success we patch the corresponding item in the cached checklist so the
// table flips without a full refetch.
export const useSetItemState = (): UseMutationResult<
  ChecklistItem,
  Error,
  SetStateArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, itemId, state }) =>
      authedFetch(
        `/releases/${encodeURIComponent(release)}/items/${encodeURIComponent(
          itemId,
        )}/state`,
        { method: 'PATCH', json: { state } },
      ),
    onSuccess: (updated, { release }) => {
      qc.setQueryData<ChecklistView | undefined>(checklistKey(release), prev =>
        prev
          ? {
              ...prev,
              items: prev.items.map(it =>
                it.id === updated.id ? updated : it,
              ),
            }
          : prev,
      );
    },
  });
};
